import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Users,
  DollarSign,
  Zap,
  AlertTriangle,
  CalendarDays,
  UserPlus,
  QrCode,
  CreditCard,
  TrendingUp,
  Activity,
  RefreshCw,
} from 'lucide-react'
import { reports } from '../api/endpoints.js'
import useAuthStore from '../store/authStore.js'

function StatCard({ icon: Icon, label, value, sub, accent, loading }) {
  return (
    <div className="stat-card flex items-start gap-4">
      <div
        className={`p-3 rounded-xl ${
          accent === 'green'
            ? 'bg-[rgb(var(--ok))]/12 text-[rgb(var(--ok))]'
            : accent === 'red'
            ? 'bg-[rgb(var(--danger))]/12 text-[rgb(var(--danger))]'
            : accent === 'neutral'
            ? 'bg-surface-3 text-text-muted'
            : 'bg-primary/12 text-primary'
        }`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-text-muted text-xs font-medium uppercase tracking-wider truncate">
          {label}
        </p>
        {loading ? (
          <div className="h-7 w-24 bg-surface-3 rounded animate-pulse mt-1" />
        ) : (
          <p className="text-2xl font-bold text-text-primary mt-0.5">
            {value ?? '—'}
          </p>
        )}
        {sub && (
          <p className="text-text-dim text-xs mt-0.5">{sub}</p>
        )}
      </div>
    </div>
  )
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const staff = useAuthStore((s) => s.staff)

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['reports', 'dashboard'],
    queryFn: () => reports.dashboard().then((r) => r.data),
    refetchInterval: 60_000,
  })

  const stats = [
    {
      icon: Users,
      label: 'Active Members',
      value: data?.active_members?.toLocaleString(),
      sub: `${data?.new_this_month ?? 0} new this month`,
      accent: 'brand',
    },
    {
      icon: DollarSign,
      label: 'Monthly Revenue',
      value: data?.mrr != null ? `$${data.mrr.toLocaleString()}` : null,
      sub: 'MRR',
      accent: 'green',
    },
    {
      icon: Zap,
      label: 'Check-ins Today',
      value: data?.checkins_today?.toLocaleString(),
      sub: 'gate + manual',
      accent: 'brand',
    },
    {
      icon: AlertTriangle,
      label: 'Failed Payments',
      value: data?.failed_payments?.toLocaleString(),
      sub: 'needs attention',
      accent: 'red',
    },
    {
      icon: CalendarDays,
      label: 'Classes Today',
      value: data?.classes_today?.toLocaleString(),
      sub: 'scheduled',
      accent: 'neutral',
    },
    {
      icon: UserPlus,
      label: 'New This Month',
      value: data?.new_this_month?.toLocaleString(),
      sub: 'new members',
      accent: 'neutral',
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Good {getGreeting()},{' '}
            <span className="text-primary">{staff?.first_name ?? 'Coach'}</span>
          </h1>
          <p className="text-text-muted text-sm mt-0.5">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isRefetching}
          className="btn-secondary flex items-center gap-2 text-sm"
        >
          <RefreshCw
            className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-[rgb(var(--danger-bg))] border border-[rgb(var(--danger-border))] text-[rgb(var(--danger))] rounded-xl px-4 py-3 text-sm">
          Failed to load dashboard data. Backend may be unavailable.
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
        {stats.map((s) => (
          <StatCard key={s.label} {...s} loading={isLoading} />
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">
          Quick Actions
        </h2>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => navigate('/gate')}
            className="card flex flex-col items-center gap-2 py-5 hover:border-primary/50 hover:bg-surface-3 transition-all duration-150 group"
          >
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <QrCode className="w-5 h-5 text-primary" />
            </div>
            <span className="text-sm font-medium text-text-primary">
              Check In Member
            </span>
          </button>
          <button
            onClick={() => navigate('/members')}
            className="card flex flex-col items-center gap-2 py-5 hover:border-primary/50 hover:bg-surface-3 transition-all duration-150 group"
          >
            <div className="w-10 h-10 bg-primary/12 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <UserPlus className="w-5 h-5 text-primary" />
            </div>
            <span className="text-sm font-medium text-text-primary">
              Add Member
            </span>
          </button>
          <button
            onClick={() => navigate('/billing')}
            className="card flex flex-col items-center gap-2 py-5 hover:border-primary/50 hover:bg-surface-3 transition-all duration-150 group"
          >
            <div className="w-10 h-10 bg-primary/12 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <CreditCard className="w-5 h-5 text-primary" />
            </div>
            <span className="text-sm font-medium text-text-primary">
              Quick Charge
            </span>
          </button>
        </div>
      </div>

      {/* Recent Activity Placeholder */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">
            Recent Activity
          </h2>
          <button
            onClick={() => navigate('/gate')}
            className="text-xs text-primary hover:text-primary-light transition-colors"
          >
            View gate log →
          </button>
        </div>
        <div className="card">
          <div className="flex items-center justify-center py-10 gap-3 text-text-dim">
            <Activity className="w-5 h-5" />
            <span className="text-sm">
              Live activity feed — check the Gate page for real-time access logs
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

function getGreeting() {
  const h = new Date().getHours()
  if (h < 12) return 'morning'
  if (h < 17) return 'afternoon'
  return 'evening'
}
